/**
 * MUI theme settings
 */
import {
  createTheme as createMuiTheme,
  PaletteColor,
  PaletteColorOptions,
  PaletteOptions,
  responsiveFontSizes,
} from '@mui/material/styles';
import { themeBaseOptions } from 'theme/themeBaseOptions';
import { overrideComponents } from 'theme/componentOverrides';
import { CustomThemeOptions, customThemeOptions } from 'theme/customThemeOptions';
import { deepmerge } from '@mui/utils';
import { DeepPartial } from 'utils/types';
import { getThemePalette } from 'theme/palette';
// noinspection ES6UnusedImports
import {} from '@mui/x-date-pickers/themeAugmentation';

export type Branding = {
  palette?: PaletteOptions;
  components?: DeepPartial<CustomThemeOptions>;
};

export const createThemeCore = (brandingOptions: Branding = {}) => {
  const palette = deepmerge(
    getThemePalette(brandingOptions.palette?.mode),
    brandingOptions.palette
  );

  const customOptions = deepmerge(customThemeOptions, brandingOptions.components);

  return createMuiTheme({
    ...themeBaseOptions,
    palette,
    ...customOptions,
  });
};

export const createTheme = (brandingOptions: Branding) => {
  const themeCore = createThemeCore(brandingOptions);

  return responsiveFontSizes(
    createMuiTheme(themeCore, {
      components: overrideComponents(themeCore),
    })
  );
};

declare module '@mui/material/styles' {
  interface Theme {
    room: {
      background: string;
      tileBackground: string;
      lightBackground: string;
      tileGap: number;
    };
    toolbar: {
      width: string;
      background: string;
      badge: {
        active: {
          background: string;
        };
      };
    };
    input: {
      lineHeight: number;
    };
    toolbarPanel: {
      width: string;
    };
    topBar: {
      height: string;
    };
    layout: {
      containerPadding: string;
      innerPadding: string;
    };
  }

  interface ThemeOptions {
    room?: {
      background: string;
      tileBackground: string;
      lightBackground: string;
      tileGap: number;
    };
    toolbar?: {
      width: string;
      background?: string;
      badge?: {
        active: {
          background: string;
        };
      };
    };
    input?: {
      lineHeight?: number;
    };
    toolbarPanel?: {
      width: string;
    };
    topBar?: {
      height: string;
    };
    layout?: {
      containerPadding: string;
      innerPadding: string;
    };
  }

  interface ZIndex {
    popper: number;
    overlay: number;
  }

  interface Palette {
    grayscale: PaletteColor;
  }

  interface PaletteOptions {
    grayscale?: PaletteColorOptions;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    grayscale: true;
  }
}
